// GENERAL
import { AnimatePresence, motion } from "framer-motion";
import React, { useState, useRef, useEffect } from "react";
import {
	slideOutMenuOverlayVariant,
	slideOutMenuVariants,
} from "../../../../motionVariants";

// TYPES
import { InventoryItemsModalContentTemplateProps } from "../../../Items/types";

// COMPONENTS
import {
	BaseButton,
	BaseContainer,
	BasePill,
	BaseText,
	BaseToolTip,
	EventLog,
	ToggleButton,
	VerticalImageCarousel,
	ModalContentSection,
	ImageComponent,
} from "../../../../components";

// UTILS
import { getFontAwesomeIcon } from "../../../../utils";

// VIEW MODEL
import { DUMMYINVENTORYITEMS } from "../../viewModel";

// ASSETS
import placeholderImage from "../../../../assets/placeholder-no-image-available.png";

// API
import { getItemDetails } from "../../api/InventoryApi";
import SpecsSection from "../groupBymodal/SpecsSection";

const specCategories = [
	{
		title: "Physical Characteristics",
		content: (
			<ul>
				<li>Dimensions: {"dimensions"}</li>
				<li>Weight: {"weight"}</li>
				<li>Material: {"material"}</li>
			</ul>
		),
	},
	{
		title: "Display",
		content: (
			<ul>
				<li>Size: {"displaySize"}</li>
				<li>Resolution: {"displayResolution"}</li>
			</ul>
		),
	},
	{
		title: "Performance",
		content: (
			<ul>
				<li>CPU: {"cpu"}</li>
				<li>RAM: {"ram"}</li>
			</ul>
		),
	},
	{
		title: "Battery & Charging",
		content: (
			<ul>
				<li>Capacity: {"batteryCapacity"}</li>
				<li>Fast-charge: {"fastCharging"}</li>
			</ul>
		),
	},
	{
		title: "Connectivity",
		content: (
			<ul>
				<li>Wi-Fi: {"wifi"}</li>
				<li>Bluetooth: {"bluetooth"}</li>
			</ul>
		),
	},
];
type FieldDef = {
	id: string;
	label: string;
	valueKey: string; // e.g. "manufacturer.name" or "partNumber"
	placeholder?: string;
	inputType?: string;
	prefix?: string;
	heading?: string;
};

const sections: Record<string, FieldDef[]> =
	DUMMYINVENTORYITEMS.DUMMYINVENTORYITEMFIELDS.reduce(
		(acc, secObj) => {
			const key = Object.keys(secObj)[0];
			acc[key] = (secObj as any)[key] as FieldDef[];
			return acc;
		},
		{} as Record<string, FieldDef[]>,
	);

const getDeep = (obj: any, path: string): any =>
	path.split(".").reduce((o, k) => (o == null ? undefined : o[k]), obj);

type Image = {
	highImage: {
		sourceUrl: string;
	};
	lowImage: {
		sourceUrl: string;
	};
	mediumImage: {
		sourceUrl: string;
	};
};

const InventoryItemsViewModalContent: React.FC<
	InventoryItemsModalContentTemplateProps
> = ({
	modalRef,
	hasBorder = false,
	borderColor = "primary",
	parentData,
	onClose,
	parentSelectsData,
	setIsLoading,
}) => {
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
	const [toggleStatus, setToggleStatus] = useState<boolean>(false);
	const [toggleDisabled, setToggleDisabled] = useState<boolean>(true);
	const [activeUuid, setActiveUuid] = useState<string | null>(null);
	const [showSpecs, setShowSpecs] = useState(false);
	const item = parentData;

	const initialItemRef = useRef(item);

	const handleClose = () => {
		onClose();
	};

	const toggleMenu = () => {
		setIsMenuOpen(!isMenuOpen);
	};

	const toggleSpecs = () => {
		setShowSpecs(!showSpecs);
	};

	const modalBorderClassNames = ` overflow-scroll rounded-lg  bg-white min-w-90%, border-2  max-h-[95vh] px-8 pt-2 ${
		isMenuOpen ? " overflow-hidden pt-0" : ` pt-0 `
	}  ${hasBorder ? `border-${borderColor}` : "border-transparent"}`;
	// HEADER CLASSES:
	const headerContainerClassNames =
		"flex flex-col items-between justify-between sticky top-[-5px] bg-white max-lg:flex-col min-h-32 pt-2";
	const headerTitleAndButtonsContainerClassNames =
		"flex items-start max-lg:flex-col justify-between w-full pb-2";
	const headerTitlesContainerClassNames =
		"flex w-1/2 max-lg:w-full max-lg:ml-1 justify-between";
	const headerButtonContainerClassNames =
		"gap-2 flex w-1/2 justify-end items-center  max-lg:pt-3 max-lg:justify-start";
	const mobileExitButtonClassNames = "hidden max-lg:flex justify-end py-2";
	// SLIDEOUT MENU CLASSES:
	const slideOutMenuClassNames = `slide-out-menu bg-white w-1/2 min-w-[300px] max-w-[500px] absolute top-0 left-0 z-40`;
	const overlayClassNames = `bg-black absolute z-1 w-full left-0 top-0 cursor-pointer min-h-full`;
	const overlayHeight = modalRef.current
		? modalRef.current.scrollHeight + "px"
		: "100%";
	const menuItemClassNames =
		"sticky min-h-[100vh] max-h-[100vh] mb-20 bg-white mt-20";
	const eventLogTitle = "View Log";

	if (!item) {
		return null; // or your loading placeholder
	}

	const itemImages = item.itemImages
		.filter((img: Image) => img?.highImage?.sourceUrl)
		.map((img: Image) => img?.highImage?.sourceUrl);

	return (
		<div
			ref={modalRef}
			className={modalBorderClassNames}
			// onClick={handleModalClick}
		>
			<header
				className={headerContainerClassNames}
				aria-label="Items modal header."
			>
				<div className="flex flex-col px-1 py-4">
					<div className={headerTitleAndButtonsContainerClassNames}>
						<div className={headerTitlesContainerClassNames}>
							<div className="flex items-center">
								{
									<BasePill
										text={
											<BaseText
												size={"text-sm"}
												color={"text-slate-950"}
												text={"Item"}
												fontFamily={"font-omnes-medium"}
												tag={"h1"}
											/>
										}
										firstIcon={getFontAwesomeIcon(
											"box",
											"solid",
										)}
										hoverBackgroundColor="none"
										additionalClasses="px-2.5 py-1 h-26px"
										shape="rounded-md"
										color="bg-supply-blue-50"
										borderColor="transparent"
										type={"span"}
										hasMobileStyle={false}
									/>
								}
								<div className="px-2 text-left">
									{
										<BaseText
											size={"text-base"}
											color={"text-gray-600"}
											text={`${
												item?.manufacturer?.name || "—"
											} - Part #: ${item?.partNumber}`}
											fontFamily={"font-omnes-medium"}
											tag={"h2"}
										/>
									}
								</div>
							</div>
							<div className={mobileExitButtonClassNames}>
								{
									<BaseButton
										text=""
										additionalClasses={`p-2 mx-4px`}
										iconAriaLabel="Close"
										icon={getFontAwesomeIcon(
											"Xmark",
											"regular",
										)}
										as="button"
										onClick={handleClose}
										iconClasses="flex items-center text-lg pr-0 size-3 text-navy-300 hover:text-navy-500"
									/>
								}
							</div>
						</div>
						<div className="hidden text-left max-lg:flex ml-1">
							<BaseText
								size={"text-22px"}
								color={"text-navy-500"}
								text={
									item?.description &&
									item.description !== "—"
										? item.description
										: "Item Title Not Provided"
								}
								fontFamily={"font-omnes-medium"}
								tag={"h2"}
							/>
						</div>
						<div className={headerButtonContainerClassNames}>
							<ToggleButton
								initialStatus={toggleStatus}
								onClick={() => {
									setToggleStatus(!toggleStatus);
								}}
								textPosition={"right"}
								textSize={"md"}
								activeColorBackground="bg-mint-600"
								inactiveColorBackground="bg-crimson-500"
								activeTextColor="text-mint-700"
								inactiveTextColor="text-crimson-700"
								hasDisabledStatus={true}
								additionalClasses={`w-1/4 py-2`}
								hasDivider={true}
								fontFamily="omnes-medium"
								activeLabel={"Active"}
								inactiveLabel={"Inactive"}
								id={"isActive"}
								pillHeight="h-7"
								smallToggle={false}
								borderStyle={false}
							/>

							<BaseButton
								text="View Log"
								bgColor="bg-white"
								borderColor="border-1 border-gray-200"
								hoverBackground="hover:bg-navy-100"
								additionalClasses={`text-nowrap py-1 px-3 mx-5px max-sm:size-8 max-sm:justify-center text-14px text-navy-500`}
								shape="rounded-md"
								iconClasses="pr-2 max-sm:pr-0  text-navy-500"
								icon={getFontAwesomeIcon("clock", "regular")}
								iconBreakpoint="max-lg:pr-0"
								as="button"
								textBreakpoint="max-lg:hidden"
								onClick={toggleMenu}
								fontFamily="font-omnes-medium"
							/>

							{/* TODO: commenting this out because i do not think the open in a new tab works consistently with this modal yet
							 <BaseToolTip
								message="Open in New Tab"
								toolTipPlacement="top"
								children={
									<BaseButton
										borderColor="none"
										icon={getFontAwesomeIcon(
											"ArrowUpRightFromSquare",
											"regular"
										)}
										onClick={() =>
											window.open(
												window.location.href,
												"_blank"
											)
										}
										as="button"
										iconClasses="text-lg"
										iconAriaLabel="Open in New Tab"
										additionalClasses="h-8 w-8 text-gray-400 hover:text-gray-700"
									/>
								}
							/> */}
							<BaseToolTip
								toolTipPlacement="top"
								message={"Close"}
								children={
									<BaseButton
										borderColor="none"
										icon={getFontAwesomeIcon(
											"Xmark",
											"regular",
										)}
										onClick={handleClose}
										as="button"
										iconClasses="text-lg"
										iconAriaLabel="Open in New Tab"
										additionalClasses="h-8 w-8 text-gray-400 hover:text-gray-700 max-lg:hidden"
									/>
								}
							/>
						</div>
					</div>
					<div className="flex text-left max-lg:hidden">
						<BaseText
							size={"text-22px"}
							color={"text-navy-500"}
							text={
								item?.description && item.description !== "—"
									? item.description
									: "Item Title Not Provided"
							}
							fontFamily={"font-omnes-medium"}
							tag={"h2"}
						/>
					</div>
					<div className="mt-2 mb-3.5">
						{showSpecs ? (
							<div
								className="hover:text-supply-blue-300 text-primary text-sm cursor-pointer flex items-center"
								onClick={toggleSpecs}
							>
								<span className="pr-1">
									{getFontAwesomeIcon(
										"chevronLeft",
										"regular",
									)}
								</span>
								<span>Back</span>
							</div>
						) : (
							<>
								<BaseButton
									text="View Specs"
									bgColor="bg-white"
									hoverBackground="hover:bg-primaryHoverLight"
									fontColor="text-primary"
									borderColor="border-1 border-primary"
									additionalClasses={`text-nowrap max-sm:size-8 max-sm:justify-center text-14px  px-2`}
									shape="rounded"
									icon={getFontAwesomeIcon(
										"clipboardList",
										"regular",
									)}
									iconBreakpoint="flex pr-1"
									as="button"
									fontFamily="font-omnes-medium"
									buttonSize=" h-26px"
									iconClasses="text-primary"
									onClick={toggleSpecs}
								/>
							</>
						)}
					</div>
				</div>
			</header>

			{showSpecs ? (
				<>
					<SpecsSection categories={specCategories} />
				</>
			) : (
				<>
					<div className="flex justify-around w-full max-md:flex-col gap-10">
						{/*** LEFT COLUMN: only the FIRST section + image ***/}
						<BaseContainer
							columnContainerClasses="flex flex-col w-2/5 max-md:w-full mb-30"
							columnShape="none"
							borderColor="transparent"
							isDisabled={false}
						>
							{(item.itemImages?.length ?? 0) > 0 ? (
								<div className="flex items-start justify-center flex-col mb-6">
									<VerticalImageCarousel
										imageUrls={itemImages}
										containerMaxHeight="max-h-96"
										containerMinHeight="min-h-96"
										enabled
									/>
								</div>
							) : (
								<div className="flex items-center justify-center flex-col mb-6">
									<ImageComponent
										imageUrl={placeholderImage}
										altText="No Image Available"
										className="size-54"
									/>
								</div>
							)}

							{(() => {
								const firstKey = Object.keys(sections)[0];
								const [headingDef, ...fieldDefs] =
									sections[firstKey];

								// 1) Build a flat map of string values
								const flat: Record<string, string> = {};
								fieldDefs.forEach((f) => {
									const raw = getDeep(item, f.valueKey);
									let val: string;
									if (raw == null) {
										val = "—";
									} else if (Array.isArray(raw)) {
										val = raw.join(", ");
									} else if (typeof raw === "object") {
										const obj = raw as any;
										val =
											obj.name ??
											obj.uuid ??
											JSON.stringify(obj);
									} else {
										val = String(raw);
									}
									flat[f.id] = val;
								});
								const fields = fieldDefs.map((f) => ({
									...f,
									valueKey: `_flat.${f.id}`,
								}));

								const dataWithFlat = {
									...item,
									_flat: flat,
								};

								return (
									<ModalContentSection
										sectionTitle={headingDef.heading}
										data={dataWithFlat}
										dataLabel={firstKey}
										fields={fields}
										itemContainerClasses="w-full py-1"
										sectionContainerClasses="flex flex-col items-start"
										detailsContainerClasses="flex flex-col items-start w-full "
										mobileBreakpoint="max-md:flex-col"
										valueType="detail"
										isDisabled={false}
									/>
								);
							})()}
						</BaseContainer>

						{/*** RIGHT COLUMN ***/}
						<BaseContainer
							columnContainerClasses="flex flex-col w-3/5 max-md:w-full py-2.5 px-1.5 bg-navy-100"
							columnShape="rounded-full"
							borderColor="transparent"
							isDisabled={false}
						>
							{Object.entries(sections)
								.slice(1)
								.map(([sectionKey, defs]) => {
									const [headingDef, ...fieldDefs] = defs;
									const fields = fieldDefs.map((f) => {
										const raw = getDeep(item, f.valueKey);
										if (raw == null) {
											return {
												...f,
												value: "—",
											};
										}
										if (Array.isArray(raw)) {
											return {
												...f,
												value: raw.join(", "),
											};
										}
										if (typeof raw === "object") {
											const obj = raw as any;
											return {
												...f,
												value:
													obj.name ??
													obj.uuid ??
													JSON.stringify(obj),
											};
										}
										return { ...f, value: String(raw) };
									});

									const showHeader =
										headingDef.heading !==
										(fieldDefs[0]?.label ?? "");
									return (
										<ModalContentSection
											key={sectionKey}
											sectionTitle={
												showHeader
													? headingDef.heading
													: undefined
											}
											data={item}
											dataLabel={sectionKey}
											fields={fields}
											itemContainerClasses="w-full py-1"
											sectionContainerClasses="flex flex-col items-start py-2 px-4"
											detailsContainerClasses={
												sectionKey === "INVENTORY"
													? "grid grid-cols-2 items-start w-full"
													: "flex flex-col items-start w-full"
											}
											mobileBreakpoint="max-md:flex-col"
											valueType="detail"
											isDisabled={false}
										/>
									);
								})}
						</BaseContainer>
					</div>
				</>
			)}

			<>
				<AnimatePresence>
					{isMenuOpen && (
						<motion.div
							initial={slideOutMenuVariants.initial}
							animate={slideOutMenuVariants.animate}
							exit={slideOutMenuVariants.exit}
							transition={slideOutMenuVariants.transition}
							className={slideOutMenuClassNames}
							style={{
								height: overlayHeight,
							}}
						>
							<div className={menuItemClassNames}>
								<EventLog
									title={eventLogTitle}
									isOpen={isMenuOpen}
									parentData={parentData}
									slug={"inventory"}
								/>
							</div>
						</motion.div>
					)}
				</AnimatePresence>
				<AnimatePresence>
					{isMenuOpen && (
						<motion.div
							initial={slideOutMenuOverlayVariant.initial}
							animate={slideOutMenuOverlayVariant.animate}
							exit={slideOutMenuOverlayVariant.exit}
							transition={slideOutMenuOverlayVariant.transition}
							onClick={toggleMenu}
							className={overlayClassNames}
							style={{
								height: overlayHeight,
							}}
						></motion.div>
					)}
				</AnimatePresence>
			</>
		</div>
	);
};
export default InventoryItemsViewModalContent;
